import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, SearchableSelect, useToast, type SearchableSelectOption } from '@kss/ui';
import { apiClient } from '@kss/api';
import { RefreshCw, ShieldCheck, UserCog } from 'lucide-react';

/**
 * Assign roles to a user. The User registry form has no role picker (roles
 * live in the separate `/authorization/UserRole` join table), so this page
 * lets an admin pick a user and tick the roles they should have — adding /
 * removing `UserRole` rows accordingly.
 */

interface Row { [k: string]: unknown }
function listOf(d: unknown): Row[] {
  const b = (d ?? {}) as Record<string, unknown>;
  return (Array.isArray(b) ? b
    : Array.isArray(b.items) ? b.items
    : Array.isArray(b.data) ? b.data
    : Array.isArray((b.data as Record<string, unknown>)?.items) ? (b.data as { items: unknown[] }).items
    : []) as Row[];
}
const sid = (v: unknown) => String(v ?? '');

export default function UserRolesPage() {
  const toast = useToast();
  const [users, setUsers] = useState<Row[]>([]);
  const [roles, setRoles] = useState<Row[]>([]);
  const [userRoles, setUserRoles] = useState<Row[]>([]);
  const [userId, setUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingRole, setSavingRole] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [u, r, ur] = await Promise.all([
        apiClient.get('/identity/User').then((x) => listOf(x.data)).catch(() => []),
        apiClient.get('/authorization/Role').then((x) => listOf(x.data)).catch(() => []),
        apiClient.get('/authorization/UserRole').then((x) => listOf(x.data)).catch(() => []),
      ]);
      setUsers(u); setRoles(r); setUserRoles(ur);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const userOptions = useMemo<SearchableSelectOption[]>(
    () => users.map((u) => ({
      value: sid(u.id),
      label: sid(u.username ?? u.name ?? u.code ?? `#${u.id}`),
      description: u.email ? sid(u.email) : undefined,
    })).filter((o) => o.value),
    [users],
  );

  // role id → the UserRole join row for the selected user (if assigned).
  const assigned = useMemo(() => {
    const m = new Map<string, Row>();
    for (const ur of userRoles) {
      if (sid(ur.userId) === userId) m.set(sid(ur.roleId), ur);
    }
    return m;
  }, [userRoles, userId]);

  async function toggleRole(role: Row, on: boolean) {
    if (!userId) { toast.error('Pick a user first', 'Select a user to manage their roles.'); return; }
    const roleId = sid(role.id);
    setSavingRole(roleId);
    try {
      if (on) {
        await apiClient.post('/authorization/UserRole', {
          code: `UR-${userId}-${roleId}`,
          name: `${userOptions.find((o) => o.value === userId)?.label ?? userId} · ${sid(role.name ?? role.code)}`,
          status: 0,
          userId: Number(userId) || userId,
          roleId: Number(roleId) || roleId,
        });
        toast.success('Role granted', sid(role.name ?? role.code));
      } else {
        const join = assigned.get(roleId);
        if (join?.id != null) {
          await apiClient.delete(`/authorization/UserRole/${join.id}`);
          toast.success('Role removed', sid(role.name ?? role.code));
        }
      }
      // Refresh the join table so the checkbox state reflects the server.
      setUserRoles(listOf((await apiClient.get('/authorization/UserRole')).data));
    } catch (e) {
      toast.error('Update failed', e instanceof Error ? e.message : 'Try again');
    } finally { setSavingRole(null); }
  }

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <UserCog className="text-primary" size={20} />
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Assign user roles</h1>
          <p className="text-xs text-content-muted">Pick a user, then tick the roles they should have. Changes save immediately.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      <Card className="p-4">
        <div className="mb-4 max-w-md">
          <span className="mb-1 block text-xs font-medium text-content-secondary">User</span>
          <SearchableSelect value={userId} onChange={setUserId} options={userOptions} placeholder="Select a user" searchPlaceholder="Search users…" />
        </div>

        {!userId ? (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-content-muted">
            Select a user above to manage their roles.
          </div>
        ) : roles.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-content-muted">No roles defined.</div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {roles.map((role) => {
              const rid = sid(role.id);
              const on = assigned.has(rid);
              return (
                <label key={rid} className={`flex cursor-pointer items-start gap-2 rounded-md border p-3 ${on ? 'border-primary bg-primary/5' : 'border-border bg-surface-card hover:border-primary'}`}>
                  <input
                    type="checkbox"
                    className="mt-0.5 accent-primary"
                    checked={on}
                    disabled={savingRole === rid}
                    onChange={(e) => void toggleRole(role, e.target.checked)}
                  />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1 text-sm font-medium text-content-primary">
                      <ShieldCheck size={13} className={on ? 'text-primary' : 'text-content-muted'} /> {sid(role.name ?? role.code)}
                    </span>
                    {role.description ? <span className="line-clamp-2 text-[11px] text-content-muted">{sid(role.description)}</span> : null}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
